import React from 'react'

import { Pencil } from 'lucide-react'

import { ZoneStateStatus } from '@/core/api/entities/zone/state'
import { Zone } from '@/core/api/entities/zone/zone'
import { usePermission } from '@/core/hooks/usePermission'
import { useTranslation } from '@/core/i18n/useTranslation'
import { ActivateBadge } from '@/features/zones/components/ActivateBadge'
import IdBadge from '@/features/zones/components/IdBadge'
import { useZoneStatus } from '@/features/zones/hooks/useZoneStatus'
import { Badge } from '@/shared/components/atoms/Badge/Badge'
import { Button } from '@/shared/components/atoms/Button'
import Icon from '@/shared/components/atoms/Icon'
import { cn, lastSeenText } from '@/shared/utils/helpers'

interface ZoneInfoHeaderProps {
	zone: Zone
	onEdit?: () => void
	onConnectZone?: () => void
}

/**
 * ZoneInfoHeader Component
 *
 * Displays comprehensive zone information in a compact header format.
 * Combines identity (ID, name, location), status, technical details, and edit functionality.
 *
 * Layout:
 * - Top row: ID + Status badges (left) | OS/App version (right)
 * - Middle: Location/Account name
 * - Bottom: Zone name + Edit button
 */
export const ZoneInfoHeader: React.FC<ZoneInfoHeaderProps> = ({
	zone,
	onEdit,
	onConnectZone,
}) => {
	const { t } = useTranslation('zones')
	const { t: tCommon } = useTranslation('common')
	const { statusBadges } = useZoneStatus(zone)
	const { features } = usePermission()

	const lastSeen = zone.state?.lastSeen
	const isZoneInstalled =
		zone.state?.status !== ZoneStateStatus.NOT_INSTALLED &&
		zone.state?.status !== ZoneStateStatus.NOT_REACTIVATED
	const shouldShowConnectButton =
		!isZoneInstalled && zone.hasPendingCode && zone.code

	return (
		<div className="mb-6 md:mb-3 md:border md:rounded-xl md:px-6 md:py-5 md:bg-card-dark">
			<div className="flex flex-col items-start justify-between gap-4 mb-2">
				<div className="flex w-full md:items-center justify-between gap-3">
					<div className="flex flex-wrap items-center gap-2">
						<IdBadge
							id={zone.id}
							boxNumber={zone.getActiveBoxAllocation()?.boxNumber}
						/>

						{statusBadges.map((badge, index) => {
							const Icon = badge.icon
							return (
								<Badge
									key={index}
									variant="zones"
									className={cn(badge.className, 'font-medium px-2 py-0.5 h-6')}
								>
									<Icon className="h-3 w-3" />
									{badge.label}
								</Badge>
							)
						})}

						{zone.hasPendingCode && zone.code && (
							<ActivateBadge
								status={zone.state?.status || ZoneStateStatus.NOT_INSTALLED}
								code={zone.code}
							/>
						)}
					</div>

					{shouldShowConnectButton && onConnectZone && (
						<Button
							variant="noXPadding"
							onClick={onConnectZone}
							className="h-9 rounded-full bg-white text-black hover:bg-gray-100 md:hidden"
						>
							Connect
						</Button>
					)}
				</div>

				<div className="hidden md:flex items-center gap-4 text-xs text-muted-foreground">
					{!zone.state?.isOnline() && lastSeen && (
						<div className="flex items-center gap-1.5">
							<Icon name="wifiX" width="16px" height="16px" />
							<span>
								{t('details.header.lastSeen')}: {lastSeenText(lastSeen)}
							</span>
						</div>
					)}
					<div className="flex items-center gap-1.5">
						<Icon name="monitor" width="16px" height="16px" />
						<span>
							{t('details.header.osVersion')}: {zone.state?.osVersion || 'N/A'}
						</span>
					</div>
					<div className="flex items-center gap-1.5">
						<Icon name="deviceMobile" width="16px" height="16px" />
						<span>
							{t('details.header.appVersion')}:{' '}
							{zone.state?.appVersion || 'N/A'}
						</span>
					</div>
				</div>
			</div>

			<div className="text-xs md:text-sm text-muted-foreground font-normal leading-[1.4]">
				{zone.accountName ||
					zone.location?.name ||
					t('details.header.noLocation')}
				{zone.location?.address && ` • ${zone.location.address}`}
			</div>

			<div className="flex items-center gap-2">
				<h2 className="text-lg md:text-xl font-medium text-foreground leading-[1.3] tracking-[-0.8px]">
					{zone.name}
				</h2>

				{onEdit && features.zone.edit && (
					<button
						onClick={onEdit}
						className="hidden md:flex flex-shrink-0 h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
						aria-label={tCommon('actions.edit')}
					>
						<Pencil className="h-4 w-4" />
					</button>
				)}
			</div>
		</div>
	)
}
